import { useCallback, useRef } from "react";

/**
 * Pointer-drag for one item, in board coordinates.
 *
 * Listeners go on the window rather than the element, so a fast drag that
 * leaves the item behind still moves it, and a drag that ends outside the
 * board still ends. Pointer events cover mouse, pen and touch with one path.
 */
export function useDraggable({ x, y, onMove, onStart, disabled }) {
  const origin = useRef(null);

  const onPointerDown = useCallback(
    (e) => {
      if (disabled) return;
      // Let a click on a control inside the item be a click, not a drag.
      if (e.target.closest("[data-no-drag]")) return;
      e.stopPropagation();
      onStart?.();
      origin.current = { px: e.clientX, py: e.clientY, x, y };

      const onPointerMove = (ev) => {
        if (!origin.current) return;
        onMove({
          x: origin.current.x + (ev.clientX - origin.current.px),
          y: origin.current.y + (ev.clientY - origin.current.py),
        });
      };
      const onPointerUp = () => {
        origin.current = null;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [x, y, onMove, onStart, disabled]
  );

  return { onPointerDown };
}
